import { writable, get } from 'svelte/store';
import { getOrAssignColor } from '../helpers/radar-helper';

type ActiveTab = 'zones' | 'furnitures' | 'config';

// Enhanced data structure for presence information
export interface PresenceData {
	deviceId: string;
	illuminance?: number;
	presenceObjects: number;
	presenceZones?: { [zoneId: string]: { num_objects: number; value: boolean } };
	timestamp: number;
	objects?: RadarObject[]; // Raw tracking objects for backward compatibility
}

// Interface for radar/presence objects with consistent color
export interface RadarObject {
	id: number | string;
	x: number;
	y: number;
	color: string; // Consistent color assigned when object is first detected
	[key: string]: any; // Allow for additional properties
}

// Socket event data structure for S4SN-0U61X device
export interface SocketPresenceEvent {
	event: string;
	device: {
		id: string;
		code: string;
	};
	status: {
		illuminance?: {
			lux: number;
		};
		presence?: {
			num_objects: number;
		};
		[key: string]: any; // Allow for dynamic presencezone keys
	};
}

function createActiveTabStore() {
	const store = writable<ActiveTab>('zones');

	return {
		subscribe: store.subscribe,
		set: store.set,
		get: () => get(store),
	};
}

function createPresenceStore() {
	const store = writable<Map<string, PresenceData>>(new Map());

	return {
		subscribe: store.subscribe,
		// Legacy method for backward compatibility with tracking objects
		append: (deviceId: string, data: any) => {
			store.update((storeMap) => {
				const newMap = new Map(storeMap);

				// Get existing data or create new
				const existing = newMap.get(deviceId) || {
					deviceId,
					presenceObjects: 0,
					timestamp: Date.now(),
					objects: [],
				};

				// Ensure objects have consistent colors assigned
				const objectsWithColors: RadarObject[] = Array.isArray(data)
					? data.map((obj: any) => ({
							...obj,
							color: obj.color || getOrAssignColor(obj.id), // Assign color if not present
						}))
					: [];

				// Update with new tracking data (maintaining backward compatibility)
				const updatedData: PresenceData = {
					...existing,
					objects: objectsWithColors,
					presenceObjects: objectsWithColors.length,
					timestamp: Date.now(),
				};

				newMap.set(deviceId, updatedData);
				return newMap;
			});
		},
		// New method to handle socket event data from S4SN-0U61X devices
		updateFromSocketEvent: (event: SocketPresenceEvent) => {
			// Only process events from S4SN-0U61X devices
			if (event.device.code !== 'S4SN-0U61X') {
				return;
			}

			const deviceId = event.device.id;

			store.update((storeMap) => {
				const newMap = new Map(storeMap);
				const existing = newMap.get(deviceId) || {
					deviceId,
					presenceObjects: 0,
					timestamp: Date.now(),
					objects: [],
				};

				// Extract data from socket event
				const presenceData: PresenceData = {
					...existing,
					deviceId,
					timestamp: Date.now(),
				};

				// Update illuminance if available (from illuminance:0 component)
				if (event.status['illuminance:0']?.lux !== undefined) {
					presenceData.illuminance = event.status['illuminance:0'].lux;
				}

				// Update presence objects count
				if (event.status.presence?.num_objects !== undefined) {
					presenceData.presenceObjects = event.status.presence.num_objects;
				}

				// Process all presence zones, excluding presencezone:200
				const presenceZones: { [zoneId: string]: { num_objects: number; value: boolean } } = {};

				Object.keys(event.status).forEach((key) => {
					// Match presencezone:{number} pattern, excluding presencezone:200
					const match = key.match(/^presencezone:(\d+)$/);
					if (match && match[1] !== '200') {
						const zoneId = match[1];
						const zoneData = event.status[key];

						if (zoneData && typeof zoneData === 'object') {
							presenceZones[zoneId] = {
								num_objects: zoneData.num_objects || 0,
								value: zoneData.value || false,
							};
						}
					}
				});

				// Only set presenceZones if we found any zones
				if (Object.keys(presenceZones).length > 0) {
					presenceData.presenceZones = presenceZones;
				}

				newMap.set(deviceId, presenceData);
				return newMap;
			});
		},
		resetTrack: (deviceId: string) => {
			store.update((storeMap) => {
				const newMap = new Map(storeMap);
				newMap.set(deviceId, {
					deviceId,
					presenceObjects: 0,
					timestamp: Date.now(),
					objects: [] as RadarObject[],
				});
				return newMap;
			});
		},
		// Get current presence count for a device
		getPresenceCount: (deviceId: string): number => {
			const data = get(store).get(deviceId);
			return data?.presenceObjects || 0;
		},
		// Get illuminance for a device
		getIlluminance: (deviceId: string): number | undefined => {
			const data = get(store).get(deviceId);
			return data?.illuminance;
		},
		// Get presence zones for a device
		getPresenceZones: (
			deviceId: string,
		): { [zoneId: string]: { num_objects: number; value: boolean } } | undefined => {
			const data = get(store).get(deviceId);
			return data?.presenceZones;
		},
		// Get specific zone data for a device
		getZoneData: (
			deviceId: string,
			zoneId: string,
		): { num_objects: number; value: boolean } | undefined => {
			const data = get(store).get(deviceId);
			return data?.presenceZones?.[zoneId];
		},
		// Get active zones (zones with value: true) for a device
		getActiveZones: (deviceId: string): string[] => {
			const zones = get(store).get(deviceId)?.presenceZones;
			if (!zones) return [];

			return Object.entries(zones)
				.filter(([, zoneData]) => zoneData.value)
				.map(([zoneId]) => zoneId);
		},
		// Legacy methods for backward compatibility
		getDeviceData: (deviceId: string) => {
			const data = get(store).get(deviceId);
			return data?.objects || [];
		},
		getAllData: () => get(store),
	};
}

export const presenceStore = createPresenceStore();
export const PresenceActiveTabStore = createActiveTabStore();
